import _ from 'lodash'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'

import { useRpi } from '@/providers/rpi/RpiProvider'

import AudioVisualizer from '@/components/ui/audio/AudioVisualizer'

import { useSrsRtcPlayer } from '@/hooks/useSrsRtcPlayer'

import DefaultAudioVisualizer from '../components/LiveAudioVisualizer'
import { DiagnosticRTCServiceHandle, DiagnosticRTCServiceProps } from '../types'
import { useDiagnosticRTC } from '../DiagnosticRTCProvider'

import { getMedicalCameras, getStethoscopes } from '@/utils/devices'
import { captureVideoScreenshot } from '../utils'

type RpiProps = DiagnosticRTCServiceProps & {
  children: React.ReactNode
}

function Rpi(props: RpiProps, ref: React.Ref<DiagnosticRTCServiceHandle>) {
  const { children, customDevices, onDevicesChange, onStreamChange, type } = props
  const { activeDiagnosticRTCService, isStreamer } = useDiagnosticRTC()
  const {
    currentDeviceId,
    currentStreams,
    devices: rpiDevices,
    error,
    getDeviceList,
    requestCurrentStream,
    setCurrentDevice,
    startStream,
    stopStream,
  } = useRpi()
  const { close, load, stream } = useSrsRtcPlayer()

  const audioRef = useRef<HTMLAudioElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)

  const [videoQuality, setVideoQuality] = useState<{ resolution: string; framerate: string } | null>(null)

  const currentStream = useMemo(() => {
    if (!currentDeviceId) return null

    return currentStreams[currentDeviceId] || null
  }, [currentDeviceId, currentStreams])
  const currentStreamRef = useRef(currentStream)

  const mockedMediaDevices = useMemo(
    () =>
      rpiDevices.map((rpiDevice) => ({
        deviceId: rpiDevice.id,
        label: rpiDevice.name,
        kind: rpiDevice.use === 'video' ? 'videoinput' : 'audioinput',
      })) as MediaDeviceInfo[],
    [rpiDevices]
  )

  const { medicalCameras, videoMediaDevices } = useMemo(() => {
    const medicalCameras = _.map(getMedicalCameras(customDevices, mockedMediaDevices), (medicalCameraDevice) => ({
      id: medicalCameraDevice._id,
      device: {
        deviceId: medicalCameraDevice.device.deviceId,
        label: medicalCameraDevice.device.name,
        kind: medicalCameraDevice.device.type,
      },
      customName: medicalCameraDevice.customName,
      iconURL: medicalCameraDevice.iconURL,
      type: medicalCameraDevice.type,
      resolutions: _.find(rpiDevices, (rpiDevice) => rpiDevice.id === medicalCameraDevice.device.deviceId)?.resolution,
    }))

    const videoMediaDevices = _.filter(
      rpiDevices.map((rpiDevice) => ({
        deviceId: rpiDevice.id,
        label: rpiDevice.name,
        kind: rpiDevice.use === 'video' ? 'videoinput' : 'audioinput',
        resolutions: rpiDevice.resolution,
      })) as Array<MediaDeviceInfo & { resolutions?: string[] }>,
      (device) => device.kind === 'videoinput'
    )

    return { medicalCameras, videoMediaDevices }
  }, [customDevices, mockedMediaDevices, rpiDevices])

  const { audioInputMediaDevices, stethoscopes } = useMemo(() => {
    const stethoscopes = _.map(getStethoscopes(customDevices, mockedMediaDevices), (stethoscopeDevice) => ({
      id: stethoscopeDevice._id,
      device: {
        deviceId: stethoscopeDevice.device.deviceId,
        label: stethoscopeDevice.device.name,
        kind: stethoscopeDevice.device.type,
      },
      customName: stethoscopeDevice.customName,
      iconURL: stethoscopeDevice.iconURL,
      type: stethoscopeDevice.type,
    }))

    const audioInputMediaDevices = _.filter(mockedMediaDevices, (device) => device.kind === 'audioinput')

    return { audioInputMediaDevices, stethoscopes }
  }, [customDevices, mockedMediaDevices])

  useImperativeHandle(ref, () => ({
    captureScreen,
    getStream,
    refreshConnection,
    renderAudio,
    renderAudioVisualizer,
    renderVideo,
    selectDevice,
    selectResolution,
    shareScreen,
    stopMicro,
    stopVideo: stopMicro,
  }))

  const captureScreen = useCallback((cb: (imageFile: File) => void) => {
    const videoElement = videoRef.current
    if (!videoElement) return
    const file = captureVideoScreenshot(videoElement)
    cb(file)
  }, [])

  const getStream = useCallback(() => stream, [stream])

  const refreshConnection = useCallback(() => {
    if (!isStreamer) requestCurrentStream()
  }, [isStreamer, requestCurrentStream])

  const renderAudio = () => <audio ref={audioRef} />

  const renderAudioVisualizer = () => {
    if (!!stream) {
      return <AudioVisualizer audioSrc={stream} sx={{ height: '200px' }} />
    }
    return <DefaultAudioVisualizer />
  }

  const renderVideo = () => (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
      <video ref={videoRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}></video>
    </Box>
  )

  const selectDevice = useCallback(
    (deviceId: string) => {
      if (currentStreamRef.current) {
        stopStream(currentStreamRef.current.streamId)
        close()
      }
      startStream(deviceId)
    },
    [close, startStream, stopStream]
  )

  const selectResolution = useCallback(
    (deviceId: string, resolution: string) => {
      if (currentStreamRef.current) {
        stopStream(currentStreamRef.current.streamId)
        close()
      }
      let framerate: string
      let [resolutionWidth, resolutionHeight] = resolution.split('x')
      let rw = parseInt(resolutionWidth)
      let rh = parseInt(resolutionHeight)
      switch (true) {
        case rw * rh >= 1920 * 1080:
          framerate = '5'
          break
        case rw * rh >= 1280 * 720:
          framerate = '20'
          break
        default:
          framerate = '30'
          break
      }
      setCurrentDevice(deviceId)
      setVideoQuality({ resolution, framerate })
    },
    [close, setCurrentDevice, stopStream]
  )

  const shareScreen = useCallback(() => {
    alert('not implemented')
  }, [])

  const stopMicro = useCallback(() => {
    if (currentStreamRef.current) {
      stopStream(currentStreamRef.current.streamId)
      close()
    }
  }, [close, stopStream])

  useEffect(() => {
    return () => {
      if (isStreamer) stopMicro()
    }
  }, [isStreamer, stopMicro])

  useEffect(() => {
    if (isStreamer) getDeviceList()
  }, [getDeviceList, isStreamer])

  useEffect(() => {
    currentStreamRef.current = currentStream
    if (currentStream) {
      load(currentStream.remoteRtcUrl)
    }
  }, [currentStream, load])

  useEffect(() => {
    if (!!currentDeviceId && !currentStream && !!videoQuality) {
      // start a new stream with the same camera but different resolution and framerate
      // need to wait a bit of time in order for camera to stop and then restart
      setTimeout(() => {
        startStream(currentDeviceId, videoQuality.resolution, videoQuality.framerate)
        setVideoQuality(null)
      }, 2000)
    }
  }, [currentDeviceId, currentStream, startStream, videoQuality])

  useEffect(() => {
    const audioElement = audioRef.current
    const videoElement = videoRef.current
    if (audioElement && type === 'audio') {
      audioElement.srcObject = stream
      audioElement.onloadedmetadata = () => {
        audioElement.play()
      }
    }
    if (videoElement && type === 'video') {
      videoElement.srcObject = stream
      videoElement.onloadedmetadata = () => {
        videoElement.play()
      }
    }
  }, [stream, type])

  useEffect(() => {
    onDevicesChange({ medicalCameras, stethoscopes, audioInputMediaDevices, videoMediaDevices })
  }, [activeDiagnosticRTCService, audioInputMediaDevices, medicalCameras, onDevicesChange, stethoscopes, videoMediaDevices])

  useEffect(() => {
    onStreamChange(stream)
  }, [onStreamChange, stream])

  return (
    <Box height="100%" position="relative">
      {children}
      {error && (
        <Box sx={{ width: '100%', position: 'absolute', bottom: '10px', p: '0 10px' }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      )}
    </Box>
  )
}

export default forwardRef(Rpi)
